/**
 * CetusEd Community Portal — React Query Hooks
 *
 * COMPLIANCE: All data fetching from the community portal uses TanStack
 *   React Query for caching, deduplication, and background refetch.
 *   Query keys follow the pattern: ['domain', 'resource', identifier].
 *   Mutations invalidate relevant queries on success.
 *
 * MIGRATION TARGET: The monolith has no community portal API. These hooks
 *   define the client-side data access layer for the new application.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

// ---------------------------------------------------------------------------
// Student documents
// ---------------------------------------------------------------------------

export interface StudentDocument {
  id: string;
  studentId: string;
  studentName: string;
  documentType: 'iep' | 'evaluation' | '504' | 'consent' | 'pwn' | 'other';
  title: string;
  status: 'draft' | 'final' | 'archived';
  createdAt: string;
  updatedAt: string;
  downloadUrl?: string;
}

export function useStudentDocuments(studentId: string) {
  return useQuery({
    queryKey: ['documents', studentId],
    queryFn: ({ signal }) => api.get<StudentDocument[]>(`/community/students/${studentId}/documents`, signal),
    enabled: !!studentId,
  });
}

export function useDocumentDetail(documentId: string) {
  return useQuery({
    queryKey: ['documents', documentId],
    queryFn: ({ signal }) => api.get<StudentDocument>(`/community/documents/${documentId}`, signal),
    enabled: !!documentId,
  });
}

// ---------------------------------------------------------------------------
// Consent forms
// ---------------------------------------------------------------------------

export interface ConsentFormData {
  id: string;
  documentId: string;
  formType: string;
  status: 'pending' | 'signed' | 'declined' | 'expired';
  requestedAt: string;
  signedAt?: string;
  signerName?: string;
}

export function usePendingConsents(studentId: string) {
  return useQuery({
    queryKey: ['consents', studentId],
    queryFn: ({ signal }) => api.get<ConsentFormData[]>(`/community/students/${studentId}/consents/pending`, signal),
    enabled: !!studentId,
  });
}

export function useSignConsent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { consentId: string; signature: string; signedAt: string }) =>
      api.post<void>(`/community/consents/${params.consentId}/sign`, params),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['consents'] });
      queryClient.invalidateQueries({ queryKey: ['documents', variables.consentId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

export interface Message {
  id: string;
  threadId: string;
  subject: string;
  body: string;
  from: string;
  fromName: string;
  to: string;
  toName: string;
  sentAt: string;
  readAt?: string;
  attachmentIds?: string[];
}

export interface MessageThread {
  id: string;
  subject: string;
  participants: Array<{ email: string; name: string }>;
  lastMessageAt: string;
  messageCount: number;
  unread: boolean;
}

export function useInbox() {
  return useQuery({
    queryKey: ['messages', 'inbox'],
    queryFn: ({ signal }) => api.get<MessageThread[]>('/community/messages', signal),
  });
}

export function useThread(threadId: string) {
  return useQuery({
    queryKey: ['messages', 'thread', threadId],
    queryFn: ({ signal }) => api.get<Message[]>(`/community/messages/${threadId}`, signal),
    enabled: !!threadId,
  });
}

export function useSendMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { threadId?: string; to: string; subject: string; body: string }) =>
      api.post<Message>('/community/messages', params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Calendar / schedule
// ---------------------------------------------------------------------------

export interface CalendarEventResponse {
  id: string;
  title: string;
  description?: string;
  start: string;
  end: string;
  location?: string;
  meetingType: string;
  status: 'confirmed' | 'tentative' | 'cancelled';
}

export function useCalendarEvents(start: string, end: string, studentId?: string) {
  return useQuery({
    queryKey: ['calendar', start, end, studentId],
    queryFn: ({ signal }) =>
      api.get<CalendarEventResponse[]>(
        `/community/calendar?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}${studentId ? `&studentId=${studentId}` : ''}`,
        signal,
      ),
    enabled: !!start && !!end,
  });
}

export function useRequestMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      studentId: string;
      preferredDates: string[];
      durationMinutes: number;
      reason: string;
      participants?: string[];
    }) => api.post<void>('/community/calendar/request', params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
    },
  });
}

// ---------------------------------------------------------------------------
// File sharing
// ---------------------------------------------------------------------------

export interface UploadedFile {
  id: string;
  filename: string;
  contentType: string;
  size: number;
  uploadedAt: string;
  category: string;
}

export function useUploadedFiles(studentId: string) {
  return useQuery({
    queryKey: ['files', studentId],
    queryFn: ({ signal }) => api.get<UploadedFile[]>(`/community/students/${studentId}/files`, signal),
    enabled: !!studentId,
  });
}

export function useUploadFile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { studentId: string; file: File; category: string }) => {
      const formData = new FormData();
      formData.append('file', params.file);
      formData.append('category', params.category);
      return api.post<UploadedFile>(`/community/students/${params.studentId}/files`, formData);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['files', variables.studentId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

export interface CommunityProfile {
  id: string;
  email: string;
  name: string;
  phone?: string;
  linkedStudents: Array<{
    id: string;
    name: string;
    school: string;
    grade: string;
    relationship: string;
  }>;
}

export function useProfile() {
  return useQuery({
    queryKey: ['profile'],
    queryFn: ({ signal }) => api.get<CommunityProfile>('/community/profile', signal),
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { name?: string; phone?: string }) =>
      api.patch<void>('/community/profile', params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export interface NotificationPreferences {
  email: {
    newDocuments: boolean;
    messages: boolean;
    meetingReminders: boolean;
    consentRequests: boolean;
  };
  sms: {
    meetingReminders: boolean;
    urgentAlerts: boolean;
  };
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: ['profile', 'notifications'],
    queryFn: ({ signal }) => api.get<NotificationPreferences>('/community/profile/notifications', signal),
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: NotificationPreferences) =>
      api.patch<void>('/community/profile/notifications', params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', 'notifications'] });
    },
  });
}
